import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Statistics from "./pages/Statistics";
import Bancos from "./pages/Bancos";
// [DISABLED 2026-04-24] import CustomerPortal from "./pages/CustomerPortal";
import WeeklyReport from "./pages/WeeklyReport";
import UserManagement from "./pages/UserManagement";
import TelegramBot from "./pages/TelegramBot";
import ClienteLogin from "./pages/ClienteLogin";
import AuditorLogin from "./pages/AuditorLogin";
import Reconciliation from "./pages/Reconciliation";
import AgentesUnited from "./pages/AgentesUnited";
import Consolidacion from "./pages/Consolidacion";
import SuperUnion from "./pages/SuperUnion";
import SuperUnionQuery from "./pages/SuperUnionQuery";
import CierreSuperUnion from "./pages/CierreSuperUnion";
import PPSTInforme from "./pages/PPSTInforme";
import CierreSemanal from './pages/CierreSemanal';
import AnalyticsRemitentes from "./pages/AnalyticsRemitentes";
import FlyersIA from "./pages/FlyersIA";
import FlyersManager from "./pages/FlyersManager";
import GrillaSemanal from "./pages/GrillaSemanal";
import DesignerPortal from "./pages/DesignerPortal";
import WhatsAppConversations from "./pages/WhatsAppConversations";
import WhatsAppChatwoot from "./pages/WhatsAppChatwoot";
import Seguridad from "./pages/Seguridad";
import { instalarStepUp } from "./lib/stepup";
import ApiPPPoker from "./pages/ApiPPPoker";
import Admin from "./pages/Admin";
import CollectorV2Module from "./modules/collector_v2/CollectorV2Module";

import Layout from "./components/Layout";
import { Toaster } from "./components/ui/sonner";
import { PortfolioProvider } from "./contexts/PortfolioContext";
import { BACKEND_URL, API_URL } from "./config";

const API = API_URL;

export { API, BACKEND_URL };

const readStoredToken = () => localStorage.getItem('token') || localStorage.getItem('access_token');

function App() {
  const [token, setToken] = useState(readStoredToken());
  const [user, setUser] = useState(() => {
    const s = localStorage.getItem('user');
    if (s) { try { return JSON.parse(s); } catch(e) { return null; } }
    return null;
  });

  const handleLogin = (newToken, userData = null) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('access_token', newToken);
    setToken(newToken);
    // Usar userData si viene del login, sino cargar de localStorage
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Setup axios interceptor for auth
  useEffect(() => {
    // Create axios request interceptor to add token to all requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const currentToken = readStoredToken();
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Huella obligatoria antes de mover plata grande (backend responde 428)
    const stepUpInterceptor = instalarStepUp(API);

    // Fetch user info if token exists
    if (token) {
      // Primero intentar cargar desde localStorage
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
      
      // Solo consultar /auth/me para admins (workers ya tienen datos del login)
      const parsed = storedUser ? JSON.parse(storedUser) : null;
      if (!parsed || parsed.role !== 'worker') {
        axios.get(`${API}/auth/me`)
          .then(response => {
            const userData = { ...response.data, role: 'admin' };
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          })
          .catch((error) => {
            // 401 = token vencido o sesión cerrada desde Seguridad → al login,
            // aunque haya datos guardados (si no, queda una pantalla que falla en todo).
            const st = error.response?.status;
            if (st === 401 || (st === 403 && !storedUser)) {
              handleLogout();
            }
          });
      }
    }

    // Cleanup: remove interceptor when component unmounts
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(stepUpInterceptor);
    };
  }, [token]);

  return (
    <div className="App">
      <BrowserRouter>
        <PortfolioProvider>
          <Routes>
            {/* Login routes */}
            <Route 
              path="/login" 
              element={!token ? <Login onLogin={handleLogin} /> : <Navigate to={
                user?.role === 'director_torneo' ? '/poker/ppst-informe' :
                user?.role === 'worker' ? (
                  user?.permissions?.includes('ppst_informe') ? '/poker/ppst-informe' :
                  user?.permissions?.includes('movimientos') ? '/dashboard' :
                  user?.permissions?.includes('consolidacion') ? '/poker/consolidacion' :
                  user?.permissions?.includes('super_union') ? '/poker/super-union' :
                  user?.permissions?.includes('ppsr_informe') ? '/poker/super-union-query' :
                  user?.permissions?.includes('agentes_united') ? '/poker/agentes-united' :
                  user?.permissions?.includes('flyers') ? '/flyers' :
                  user?.permissions?.includes('bancos') ? '/bancos' :
                  '/dashboard'
                ) : '/dashboard'
              } />} 
            />
            <Route 
              path="/register" 
              element={!token ? <Register /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/cliente/login" 
              element={!token ? <ClienteLogin onLogin={handleLogin} /> : <Navigate to="/dashboard" />} 
            />
            <Route 
              path="/auditor/login" 
              element={!token ? <AuditorLogin onLogin={handleLogin} /> : <Navigate to="/dashboard" />} 
            />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <Dashboard onLogout={handleLogout} />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/statistics" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <Statistics />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/bancos" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <Bancos />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/telegram-bot" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <TelegramBot />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/users" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <UserManagement />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/flyers-ia" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <FlyersIA />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/flyers" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <FlyersManager />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/grilla" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <GrillaSemanal />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/reconciliation" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <Reconciliation />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/consolidacion" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <Consolidacion />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/agentes-united" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <AgentesUnited />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/seguridad" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <Seguridad />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/api-pppoker" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <ApiPPPoker />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/cierre-super-union" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <CierreSuperUnion />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/super-union" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <SuperUnion />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/super-union-query" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <SuperUnionQuery />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/ppst-informe" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <PPSTInforme />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/poker/cierre-semanal" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <CierreSemanal />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            <Route 
              path="/analytics/remitentes" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <AnalyticsRemitentes />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            
            <Route
              path="/whatsapp-live" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <WhatsAppConversations />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />

            <Route
              path="/whatsapp-chatwoot" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <WhatsAppChatwoot />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />
            
            {/* Public routes */}
            {/* DESHABILITADO: Sistema viejo de access_token - Solo login con username/password
            <Route 
              path="/customer/:token" 
              element={<CustomerPortal />} 
            />
            */}
            
            />
            
            <Route 
              path="/reporte-semanal" 
              element={<WeeklyReport />} 
            />
            <Route 
              path="/designer" 
              element={<DesignerPortal />} 
            />
            
            <Route 
              path="/admin" 
              element={
                <Layout user={user} onLogout={handleLogout}>
                  <Admin />
                </Layout>
              } 
            />
            <Route path="*" element={<Navigate to="/dashboard" />} />
                      {/* PPPoker V2 Microservice (added 2026-05-07) */}
            <Route 
              path="/collector-v2/*" 
              element={
                token ? (
                  <Layout onLogout={handleLogout} user={user}>
                    <CollectorV2Module />
                  </Layout>
                ) : (
                  <Navigate to="/login" />
                )
              } 
            />

          </Routes>
        </PortfolioProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;